/**
 * Dashboard Preview — DESIGN DEMO (not wired to data).
 *
 * A look-and-feel preview of a full DevExpress-style dashboard built entirely on
 * the license-clean stack we already have: Recharts (charts) + the shared
 * <AppDataGrid> (grid widget) + KPI tiles, laid out as draggable-looking widget
 * tiles on a 12-column canvas.  It exists so the team can SEE the target design
 * before we commit to building the real widget designer (and before adding the
 * react-grid-layout drag/resize dependency).  All data here is static sample data.
 */
import { useMemo } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell,
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { fmtLocalDate } from '../../utils/datetime';
import './backlog.css';
import './dashboardDesigner.css';

// token-exempt: chart — categorical palette (data-viz), brand-aligned
const PALETTE = ['#e8670a', '#2563eb', '#16a34a', '#a855f7', '#f59e0b', '#0ea5e9', '#dc2626'];
const AXIS = { fontSize: 10, fill: '#8a8a8a' } as const;
const GRID = '#eee';

// ── Sample data ──────────────────────────────────────────────────────────────
const BY_STAGE = [
  { name: 'Missing COC', v: 12 },
  { name: 'Due Samples', v: 34 },
  { name: 'Sample Prep', v: 28 },
  { name: 'Result Entry', v: 41 },
  { name: 'Data Review', v: 19 },
  { name: 'Reporting', v: 23 },
];
const BY_CLIENT = [
  { name: 'City of Houston', v: 38 },
  { name: 'Special BacT', v: 22 },
  { name: 'AGT Labs', v: 17 },
  { name: 'Baytown Env', v: 12 },
  { name: 'Others', v: 11 },
];
const TREND = Array.from({ length: 8 }, (_, i) => ({
  name: `Wk ${i + 1}`,
  received: 40 + Math.round(Math.sin(i / 1.5) * 12) + i * 2,
  reported: 34 + Math.round(Math.cos(i / 1.7) * 10) + i * 2,
}));

interface OverdueRow { jobId: string; client: string; test: string; dueDate: string; daysOver: number; }
const OVERDUE: OverdueRow[] = [
  { jobId: 'JB260901', client: 'City of Houston', test: 'Total Coliform', dueDate: '2026-08-28', daysOver: 8 },
  { jobId: 'JB260914', client: 'Special BacT',    test: 'E. coli',        dueDate: '2026-08-30', daysOver: 6 },
  { jobId: 'JB260922', client: 'AGT Labs',        test: 'Nitrate',        dueDate: '2026-09-01', daysOver: 4 },
  { jobId: 'JB260931', client: 'Baytown Env',     test: 'TSS',            dueDate: '2026-09-02', daysOver: 3 },
  { jobId: 'JB260940', client: 'City of Houston', test: 'pH',             dueDate: '2026-09-03', daysOver: 2 },
];

const KPIS = [
  { lbl: 'OPEN JOBS',      val: '157', sub: 'Across all stages', accent: '' },
  { lbl: 'OVERDUE TESTS',  val: '23',  sub: 'Past due date',     accent: 'dp-red' },
  { lbl: 'DUE THIS WEEK',  val: '46',  sub: 'Tests due ≤ 7 days', accent: 'dp-orange' },
  { lbl: 'QC PASS RATE',   val: '97.4%', sub: 'Last 30 days',    accent: 'dp-green' },
];

function Tile({ title, span, chartClass, children }: { title: string; span: string; chartClass?: string; children: React.ReactNode }) {
  return (
    <div className={`dp-tile ${span}`}>
      <div className="dp-tile-hdr">
        <span>{title}</span>
        <span className="dp-tile-tools"><i className="ti ti-filter" /><i className="ti ti-dots-vertical" /></span>
      </div>
      <div className={`dp-tile-body ${chartClass ?? ''}`}>{children}</div>
    </div>
  );
}

export default function DashboardPreviewPage() {
  const gridCols: AppColumn<OverdueRow>[] = useMemo(() => [
    { field: 'jobId',    header: 'Job ID',   width: 110, text: r => r.jobId },
    { field: 'client',   header: 'Client',   text: r => r.client },
    { field: 'test',     header: 'Test',     width: 150, text: r => r.test },
    { field: 'dueDate',  header: 'Due Date', width: 110, text: r => fmtLocalDate(r.dueDate) || r.dueDate },
    { field: 'daysOver', header: 'Days Over', width: 100, align: 'right', text: r => String(r.daysOver),
      render: r => <span className="bl-pill warn">{r.daysOver}d</span> },
  ], []);

  return (
    <div className="bl-page">
      <div className="bl-grid-area" style={{ overflow: 'auto' }}>
        <div className="section-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <FormTitle pageId="dashpreview" title="Sample Backlog Overview" />
          <AppToolbar onRefresh={() => { /* demo */ }} />
        </div>

        {/* Orange "Design preview" banner removed (user request 2026-09-19). */}

        <div className="dp-canvas">
          {/* KPI row */}
          {KPIS.map(k => (
            <div className={`dp-kpi-tile dp-c3`} key={k.lbl}>
              <div className="dp-kpi-lbl">{k.lbl}</div>
              <div className={`dp-kpi-val ${k.accent}`}>{k.val}</div>
              <div className="dp-kpi-sub">{k.sub}</div>
            </div>
          ))}

          {/* Bar — backlog by stage */}
          <Tile title="Backlog by Stage" span="dp-c8">
            <ResponsiveContainer width="100%" height={210}>
              <BarChart data={BY_STAGE} margin={{ top: 6, right: 14, bottom: 0, left: -8 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} interval={0} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Bar dataKey="v" name="Tests" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  {BY_STAGE.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Tile>

          {/* Donut — by client */}
          <Tile title="Backlog by Client" span="dp-c4">
            <ResponsiveContainer width="100%" height={210}>
              <PieChart>
                <Pie data={BY_CLIENT} dataKey="v" nameKey="name" cx="50%" cy="50%"
                     innerRadius={44} outerRadius={72} paddingAngle={2} isAnimationActive={false}>
                  {BY_CLIENT.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </Tile>

          {/* Trend — received vs reported */}
          <Tile title="Received vs Reported (8 weeks)" span="dp-c6">
            <ResponsiveContainer width="100%" height={190}>
              <ComposedChart data={TREND} margin={{ top: 6, right: 12, bottom: 0, left: -8 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} width={30} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} iconType="plainline" />
                <Area type="monotone" dataKey="received" name="Received" stroke={PALETTE[1]} fill={PALETTE[1]} fillOpacity={0.12} strokeWidth={2} dot={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="reported" name="Reported" stroke={PALETTE[2]} strokeWidth={2} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </Tile>

          {/* Grid — overdue jobs */}
          <Tile title="Overdue Jobs" span="dp-c6">
            <AppDataGrid<OverdueRow>
              rows={OVERDUE}
              columns={gridCols}
              getRowId={r => r.jobId}
              emptyText="No overdue jobs"
            />
          </Tile>
        </div>
        <div className="dd-section-gap" />
      </div>
    </div>
  );
}
